/**
 * Utilities for managing ingestion notes stored as separate staff notes.
 *
 * Each note type (intake, general) is stored as its own staff note with a
 * header prefix like "From Case Ingestion - Intake Notes".
 */

// =============================================================================
// CONSTANTS
// =============================================================================

export const INGESTION_NOTE_PREFIX = 'From Case Ingestion - ';
export const INTAKE_NOTES_HEADER = 'From Case Ingestion - Intake Notes';
export const GENERAL_NOTES_HEADER = 'From Case Ingestion - General Notes';
export const INCIDENT_SUMMARY_HEADER = 'From Case Ingestion - Incident Summary';

// =============================================================================
// TYPES
// =============================================================================

export interface IngestionNotesData {
  intakeNotes: string;
  generalNotes: string;
}

// =============================================================================
// UTILITIES
// =============================================================================

/**
 * Check if a staff note content is an ingestion note (any type).
 */
export function isIngestionStaffNote(content: string): boolean {
  return content.startsWith(INGESTION_NOTE_PREFIX);
}

/**
 * Extract user content from a staff note with a header.
 * Returns empty string if the content doesn't start with the expected header.
 */
export function extractNoteContent(content: string, header: string): string {
  if (!content.startsWith(header)) return '';
  return content.slice(header.length).trim();
}

/**
 * Format a note with its header prefix.
 */
export function formatIngestionNote(header: string, content: string): string {
  const trimmed = content.trim();
  if (!trimmed) {
    return header;
  }
  return `${header}\n\n${trimmed}`;
}

/**
 * Check if any of the ingestion notes fields have content.
 */
export function hasIngestionNotesContent(data: IngestionNotesData): boolean {
  return (
    data.intakeNotes.trim().length > 0 || data.generalNotes.trim().length > 0
  );
}
